import React, { useState, useMemo, useCallback } from "react";
import { Checkbox } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import PropTypes from "prop-types";

import { getMassDisabled, getVisibleEntities } from "../../../../utils/formViews";
import { getRisksSearchHelp } from "../../../../utils/searchHelps";

import { createRowCellContentInner } from "../../../common/table";
import { TrashIcon } from "../../../common/icons";
import { SearchHelpInput } from "../../../common/form-controls";
import SearchHelpDialog from "../../../common/search-helps";
import { IconButton } from "../../../common/buttons";
import { EntityFormViewTable } from "../../../common/entity-form";
import useRisksTable from "./useRisksTable";
import "./style.less"

const getErrorFromValidationErrors = (row, columnId) => {
    return row && row[`error_${columnId}`];
};

// const getFilters = (searchString) => {
//     const filters = [
//         new ComplexFilter([new Filter("risk", Filter.CO, searchString), new Filter("description", Filter.CO, searchString)], ComplexFilter.OR),
//     ];

//     return filters;
// };

const RisksView = ({ risks, addRisk, deleteRisk, deleteRiskMass, editRisk, disabled, errorIndex, setErrorScroll, hasStatusField = true }) => {
    const intl = useIntl();

    const [selectedIds, setSelectedIds] = useState([]);
    const [rowAction, setRowAction] = useState(null);
    const visibleRisks = getVisibleEntities(risks);

    const risksTable = useRisksTable(visibleRisks);

    const columnTexts = useMemo(
        () => ({
            risk: intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.risks.risk" }),
            description: intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.risks.description" }),
            active: hasStatusField ? intl.formatMessage({ id: "matrix-headers-form-content.subtable-column.active" }) : null,
        }),
        [intl, hasStatusField]
    );
    const setSearchString = useCallback(
        (value) => {
            risksTable.setSearchString(value);
        },
        [risksTable]
    );

     const setFilterData = useCallback(
            (value) => {
                risksTable.setFilterData(value);
            }, [risksTable]
        )
    

    const toolbarActions = useMemo(() => {
        const massDisabled = getMassDisabled(disabled, selectedIds);

        return [
            {
                id: "mass-delete",
                title: intl.formatMessage({ id: "common.btn-delete" }),
                callback: () => {
                    deleteRiskMass(selectedIds);
                    setSelectedIds([]);
                },
                position: "end",
                variant: "contained",
                disabled: massDisabled,
            },
            {
                id: "search",
                type: "search",
                position: "end",
                value: risksTable?.searchString || "",
                callback: setSearchString,
            },
            {
                id: "add",
                title: <FormattedMessage id="risk-view.btn-create-risk" />,
                callback: () => {
                    setSearchString("");
                    setFilterData([])
                    addRisk();
                },
                position: "begin",
                disabled,
            },
        ];
    }, [addRisk, disabled, selectedIds, deleteRiskMass, intl, risksTable, setSearchString, setFilterData]);

    const createRowCellContent = useCallback(
        (row, column, selected, onSelect) => {
            const error = getErrorFromValidationErrors(row, column.id);

            switch (column.type) {
                case "search-help": {
                    return (
                        <SearchHelpInput
                            disabled={disabled}
                            error={error ? row[column.id] === error.value && error["type"] === "validation" : false}
                            errorMessage={error && error.message}
                            warning={error ? row[column.id] === error.value && error["type"] === "integration_warning" : false}
                            warningMessage={error && error.message}
                            showFormHelpers={false}
                            showInnerHelpers
                            value={row[column.id]}
                            fullWidth
                            onOpenSearchHelp={() => {
                                setRowAction({
                                    type: "search-help",
                                    row: row,
                                    field: column.id,
                                });
                            }}
                            onChange={(e) => {
                                const value = e.target.value;
                                editRisk(row, column.id, value);
                            }}
                        />
                    );
                }

                case "delete":
                    return (
                        <IconButton
                            disabled={disabled}
                            onClick={() => deleteRisk(row.id)}
                            sx={{
                                border: "1px solid #B7B7B7",
                                borderRadius: "3px",
                                width: "34px",
                                height: "34px",
                            }}
                        >
                            <TrashIcon color="#B7B7B7" width="34px" height="34px" />
                        </IconButton>
                    );

                case "boolean":
                    if (hasStatusField) {
                        return (
                            <Checkbox
                                disabled={disabled}
                                checked={row[column.id]}
                                sx={{
                                    "&.Mui-checked": {
                                        color: "gray",
                                    },
                                }}
                                onClick={() => {
                                    editRisk(row, column.id, !row[column.id]);
                                }}
                            />
                        );
                    }
                    return null;

                default:
                    return createRowCellContentInner(row, column, selected, onSelect);
            }
        },
        [editRisk, deleteRisk, disabled, hasStatusField]
    );

    const columns = useMemo(
        () => [
            {
                id: "select",
                type: "select",
            },
            {
                id: "riskId",
                title: columnTexts.risk,
                type: "search-help",
                filterable: true,
            },
            {
                id: "description",
                title: columnTexts.description,
                type: "text",
                width: "40%",
            },
            {
                id: "active",
                title: columnTexts.active,
                type: "boolean",
            },
            {
                id: "delete",
                type: "delete",
            },
        ],
        [columnTexts]
    );

    const getSearchHelpData = useCallback(
        (params, signal) => {
            switch (rowAction?.field) {
                case "riskId":
                    return getRisksSearchHelp(params, signal);
            }
        },
        [rowAction]
    );

    const searchHelpColumns = {
        riskId: [
            { id: "key", title: columnTexts.operation },
            { id: "text", title: columnTexts.description },
        ],
    };

    const handleSelectRow = useCallback((row, value) => {
        if (value) {
            setSelectedIds((selectedIds) => [...selectedIds, row.id]);
            return;
        }

        setSelectedIds((selectedIds) => selectedIds.filter((id) => id !== row.id));
    }, []);

    const handleSelectAll = useCallback(
        (value) => {
            if (!value) {
                setSelectedIds([]);
                return;
            }

            setSelectedIds(risksTable.rows.map((risk) => risk.id));
        },
        [risksTable]
    );

    return (
        <>
            <EntityFormViewTable
            className="risks-view-table "
                rows={risksTable.rows}
                columns={columns}
                filterData={risksTable.filterData}
                setFilterData={risksTable.setFilterData}
                getFilters={risksTable.getDialogFilters}
                setSearchString={setSearchString}
                getDialogFilters={risksTable.getDialogFilters}
                searchString={risksTable.searchString}
                toolbarActions={toolbarActions}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                createRowCellContent={createRowCellContent}
                errorIndex={errorIndex}
                setErrorScroll={setErrorScroll}
            />

            {rowAction?.type === "search-help" && (
                <SearchHelpDialog
                    defaultSearchString={rowAction.row[rowAction.field] || ""}
                    open={rowAction && rowAction.type === "search-help"}
                    readRows={getSearchHelpData}
                    columns={searchHelpColumns[rowAction.field]}
                    selectOneRow={true}
                    onSubmit={(selectedRow) => {
                        editRisk(rowAction.row, rowAction.field, selectedRow.key);
                        setRowAction(null);
                    }}
                    onCancel={() => {
                        setRowAction(null);
                    }}
                />
            )}
        </>
    );
};

RisksView.propTypes = {
    risks: PropTypes.arrayOf(PropTypes.object),
    addRisk: PropTypes.func,
    deleteRisk: PropTypes.func,
    deleteRiskMass: PropTypes.func,
    editRisk: PropTypes.func,
    disabled: PropTypes.bool,
    validationErrors: PropTypes.object,
    errorIndex: PropTypes.number,
    setErrorScroll: PropTypes.func,
    hasStatusField: PropTypes.bool,
};

export default RisksView;
